import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Search, Download, RefreshCw, Filter, ChevronRight, Inbox, Eye, Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from './AdminHeader';
import { listLeads, leadStats, exportLeadsUrl, hideLead, restoreLead, deleteLead, leadsBulkHide, leadsBulkRestore } from '../lib/marketingApi';
import { getToken } from '../lib/auth';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ViewTabs, ModerationMenu, RowCheck, BulkBar } from './ModerationControls';

const STATUSES = [
  { value: 'new', label: 'New', cls: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-amber-100 text-amber-700' },
  { value: 'qualified', label: 'Qualified', cls: 'bg-violet-100 text-violet-700' },
  { value: 'demo_scheduled', label: 'Demo scheduled', cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'won', label: 'Won', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'lost', label: 'Lost', cls: 'bg-rose-100 text-rose-700' },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const SEGMENT_LABEL = {
  multifamily: 'Multifamily', commercial_industrial: 'Commercial & Industrial',
  energy_brokers: 'Energy Broker', other: 'Other',
};
const CONF = { high: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-500' };

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

export default function LeadsCRM() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [segment, setSegment] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('active');
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const limit = 25;
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [view, status, segment, search]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(leads.map((l) => l.id))));
  const doBulk = async (reason) => {
    setBulkBusy(true);
    try { const r = await leadsBulkHide([...selected], reason); toast.success(`${r.hidden} moved to ${reason === 'spam' ? 'Spam' : 'Trash'}`); setSelected(new Set()); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };
  const doBulkRestore = async () => {
    setBulkBusy(true);
    try { const r = await leadsBulkRestore([...selected]); toast.success(`${r.restored} restored`); setSelected(new Set()); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
    finally { setBulkBusy(false); }
  };

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const p = reset ? 1 : page;
      const [s, res] = await Promise.all([
        leadStats(),
        listLeads({ status: status || undefined, segment: segment || undefined, search: search || undefined, view, page: p, limit }),
      ]);
      setStats(s);
      setTotal(res.total);
      setLeads((prev) => (p === 1 ? res.items : [...prev, ...res.items]));
    } catch (e) {
      toast.error('Failed to load enquiries.');
    } finally {
      setLoading(false);
    }
  }, [page, status, segment, search, view]);

  useEffect(() => { load(); }, [page]);
  useEffect(() => { setPage(1); load(true); }, [status, segment, view]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(true); };
  const reload = () => { setPage(1); load(true); };
  const doHide = async (id, reason) => { try { await hideLead(id, reason); toast.success(reason === 'spam' ? 'Marked as spam' : 'Moved to trash'); reload(); } catch (e) { toast.error('Action failed'); } };
  const doRestore = async (id) => { try { await restoreLead(id); toast.success('Restored'); reload(); } catch (e) { toast.error('Restore failed'); } };
  const doPurge = async (id) => { if (!window.confirm('Permanently delete this enquiry? This cannot be undone.')) return; try { await deleteLead(id); toast.success('Permanently deleted'); reload(); } catch (e) { toast.error('Delete failed'); } };

  const doExport = async () => {
    try {
      const r = await fetch(exportLeadsUrl(), { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error('export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'billportal_enquiries.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="enquiries-list">
      <AdminHeader
        active="enquiries"
        actions={(
          <>
            <button onClick={doExport} data-testid="leads-export-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#002B87]"><Download className="h-4 w-4" /> Export CSV</button>
            <button onClick={() => { setPage(1); load(true); }} data-testid="leads-refresh-btn" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          </>
        )}
      />

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Website enquiries</h1>
          <p className="mt-0.5 text-sm text-slate-500">Every lead captured from the marketing site</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="enquiries-stats">
            <StatCard label="All" value={stats.total} active={!status} onClick={() => setStatus('')} />
            {STATUSES.map((s) => (
              <StatCard key={s.value} label={s.label} value={stats.by_status[s.value] || 0} active={status === s.value} onClick={() => setStatus(status === s.value ? '' : s.value)} />
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <ViewTabs view={view} setView={setView} counts={{ spam: stats?.hidden_spam, deleted: stats?.hidden_deleted }} testidPrefix="enquiries" />
        </div>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <form onSubmit={onSearch} className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="leads-search" placeholder="Search name, email or company…" className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-900 outline-none focus:border-[#0035A8]" />
          </form>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="leads-segment-filter" className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-[#0035A8]">
              <option value="">All segments</option>
              {Object.entries(SEGMENT_LABEL).map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
          </div>
        </div>

        <BulkBar count={selected.size} label="enquiries" busy={bulkBusy} {...(view === 'active' ? { onSpam: () => doBulk('spam'), onTrash: () => doBulk('deleted') } : { onRestore: doBulkRestore })} onClear={() => setSelected(new Set())} testidPrefix="enquiries-bulk" />

        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-3 flex items-center gap-2">{<RowCheck checked={allSelected} onChange={toggleAll} testid="enquiries-select-all" ariaLabel="Select all" />} Name</div>
            <div className="col-span-2">Company</div>
            <div className="col-span-2">Assigned to</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Last contacted</div>
          </div>
          {leads.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
              <Inbox className="h-8 w-8" />
              <p className="text-sm">No enquiries yet. They’ll appear here as visitors submit forms.</p>
            </div>
          )}
          {leads.map((l) => (
            <div key={l.id} role="button" tabIndex={0} onClick={() => navigate(`/admin/enquiries/${l.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/enquiries/${l.id}`); }} data-testid={`lead-row-${l.id}`} className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3.5 text-left transition-colors hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
              <div className="col-span-3 min-w-0 flex items-center gap-2">
                {<RowCheck checked={selected.has(l.id)} onChange={() => toggle(l.id)} testid={`lead-select-${l.id}`} />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-semibold text-slate-900">{l.name}</span>
                    {l.enrichment && (
                      <span data-testid={`lead-conf-${l.id}`} title={`AI research: ${l.enrichment.confidence || 'low'} confidence`} className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${CONF[l.enrichment.confidence] || CONF.low}`}>
                        <Sparkles className="h-2.5 w-2.5" /> {l.enrichment.confidence || 'low'}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-slate-500">{l.email}</div>
                </div>
              </div>
              <div className="col-span-2 truncate text-sm text-slate-700">{l.company || <span className="text-slate-300">—</span>}</div>
              <div className="col-span-2 truncate text-sm">{l.owner ? <span className="text-slate-700">{l.owner}</span> : <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Unassigned</span>}</div>
              <div className="col-span-2"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusMeta(l.status).cls}`}>{statusMeta(l.status).label}</span></div>
              <div className="col-span-3 flex items-center justify-end gap-1 text-xs text-slate-500">
                <span className="hidden sm:inline" title={l.last_contacted_at ? 'Last contacted' : 'Not contacted yet'}>{l.last_contacted_at || l.booking_link_sent_at ? fmt(l.last_contacted_at || l.booking_link_sent_at) : <span className="text-slate-300">Not contacted</span>}</span>
                <ModerationMenu view={view} isAdmin={isAdmin} testid={`lead-${l.id}`} onSpam={() => doHide(l.id, 'spam')} onDelete={() => doHide(l.id, 'deleted')} onRestore={() => doRestore(l.id)} onPurge={() => doPurge(l.id)} />
              </div>
            </div>
          ))}
          {leads.length < total && (
            <div className="p-4 text-center">
              <button onClick={() => setPage((p) => p + 1)} disabled={loading} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {loading ? 'Loading…' : `Load more (${total - leads.length} left)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, active, onClick }) {
  return (
    <button onClick={onClick} className={`rounded-lg border bg-white p-3 text-left transition-all ${active ? 'border-[#0035A8] ring-1 ring-[#0035A8]' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="text-xl font-bold text-slate-900">{value}</div>
      <div className="mt-0.5 truncate text-[11px] text-slate-500">{label}</div>
    </button>
  );
}
